import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

export default function Footer({ onPrivacyClick }) {
  const { t, setLanguage } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#002a22] pt-24 sm:pt-32 pb-8" data-testid="footer">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Giant Brand Name */}
        <div className="mb-16 overflow-hidden">
          <h2
            className="text-[12vw] lg:text-[8vw] font-black text-white/[0.04] leading-none tracking-tighter select-none"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="footer-brand"
          >
            ASCIFIW3
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_4f732c3a-34ed-4f43-939a-46403ae9e699/artifacts/pnmsau6a_Background.png"
                alt="Ascifiw3 Network"
                className="h-10 w-10 rounded-full object-cover"
              />
              <span className="font-bold text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ascifiw3 Network
              </span>
            </div>
            <p className="text-zinc-500 max-w-sm leading-relaxed" data-testid="footer-tagline">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">Links</h4>
            <ul className="space-y-3">
              {Object.entries(t('footer.links')).map(([key, label]) => (
                <li key={key}>
                  {key === 'privacy' ? (
                    <button
                      onClick={onPrivacyClick}
                      className="text-zinc-400 hover:text-white transition-colors text-sm"
                      data-testid={`footer-link-${key}`}
                    >
                      {label}
                    </button>
                  ) : key === 'terms' ? (
                    <Link
                      to="/terms"
                      className="text-zinc-400 hover:text-white transition-colors text-sm"
                      data-testid={`footer-link-${key}`}
                    >
                      {label}
                    </Link>
                  ) : key === 'contact' ? (
                    <Link
                      to="/contact"
                      className="text-zinc-400 hover:text-white transition-colors text-sm"
                      data-testid={`footer-link-${key}`}
                    >
                      {label}
                    </Link>
                  ) : (
                    <a
                      href={`#${key}`}
                      className="text-zinc-400 hover:text-white transition-colors text-sm"
                      data-testid={`footer-link-${key}`}
                    >
                      {label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">Contact Us</h4>
            <a
              href="mailto:ascifiw3@proton.me"
              className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-2"
              data-testid="footer-contact-email"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              ascifiw3@proton.me
            </a>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">Social</h4>
            <div className="flex gap-4">
              {[
                { name: 'facebook', url: 'https://www.facebook.com/share/176fJnqQVY/?mibextid=wwXIfr' },
                { name: 'instagram', url: 'https://www.instagram.com/ascifiw3_network?igsh=emtiaXpnYmQweG5l&utm_source=qr' },
                { name: 'tiktok', url: 'https://www.tiktok.com/@ascifiw3_network?_r=1&_t=ZG-95VbVN2M3gO' },
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-all"
                  data-testid={`footer-social-${social.name}`}
                >
                  <SocialIcon name={social.name} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/[0.06] pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-zinc-600 text-sm" data-testid="footer-copyright">
            &copy; {year} {t('footer.copyright')}
          </p>
          <div className="flex gap-6">
            {[
              { code: 'en', name: 'English' },
              { code: 'fr', name: 'Français' },
              { code: 'es', name: 'Español' },
              { code: 'pt', name: 'Português' },
            ].map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className="text-zinc-600 text-xs hover:text-zinc-400 cursor-pointer transition-colors"
                data-testid={`footer-lang-${lang.code}`}
              >
                {lang.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ name }) {
  const paths = {
    facebook: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    instagram: 'M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.25a1.25 1.25 0 0 0-2.5 0 1.25 1.25 0 0 0 2.5 0zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z',
    tiktok: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  };

  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d={paths[name]} />
    </svg>
  );
}
